import { createContext, useContext, useState, useEffect } from 'react'

interface QuietModeContextType {
  isQuietMode: boolean
  toggleQuietMode: () => void
  isLowStimMode: boolean
  toggleLowStimMode: () => void
}

const QuietModeContext = createContext<QuietModeContextType | undefined>(undefined)

export const useQuietMode = () => {
  const context = useContext(QuietModeContext)
  if (!context) {
    throw new Error('useQuietMode must be used within a QuietModeProvider')
  }
  return context
}

export const QuietModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isQuietMode, setIsQuietMode] = useState(false)
  const [isLowStimMode, setIsLowStimMode] = useState(false)

  // Load settings from localStorage on mount
  useEffect(() => {
    const savedQuietMode = localStorage.getItem('quietMode') === 'true'
    const savedLowStimMode = localStorage.getItem('lowStimMode') === 'true'
    setIsQuietMode(savedQuietMode)
    setIsLowStimMode(savedLowStimMode)
  }, [])

  const toggleQuietMode = () => {
    const newValue = !isQuietMode
    setIsQuietMode(newValue)
    localStorage.setItem('quietMode', newValue.toString())
  }

  const toggleLowStimMode = () => {
    const newValue = !isLowStimMode
    setIsLowStimMode(newValue)
    localStorage.setItem('lowStimMode', newValue.toString())
  }

  return (
    <QuietModeContext.Provider value={{
      isQuietMode,
      toggleQuietMode,
      isLowStimMode,
      toggleLowStimMode
    }}>
      <div className={`${isLowStimMode ? 'low-stim-mode' : ''} ${isQuietMode ? 'quiet-mode' : ''}`}>
        {children}
      </div>
    </QuietModeContext.Provider>
  )
}